import {
  Component,
  OnInit,
  inject,
  viewChild,
  ChangeDetectionStrategy,
} from "@angular/core";

import { UserRepository } from "../user.repository";
import { UserService } from "../user.service";
import { User, UserStats } from "../user.model";
import { BackendErrorPipe } from "../../shared/backend/backend-error.pipe";
import { CrudAbstractListComponent } from "../../crud-abstract/crud-abstract-list.component";
import { CrudAbstractModel } from "../../crud-abstract/crud-abstract.model";
import { GridParams } from "../../shared/grid/grid-params";
import { GridSortingService } from "../../shared/grid/grid-sorting.service";
import { EmailEditorComponent } from "../../ui/email-editor/email-editor.component";
import { role } from "../role.const";
import { NotifyService } from "../../notify/notify.service";
import { EmailEditorRepository } from "../../ui/email-editor/email-editor.repository";
import { TOAST } from "../../notify/toast/toast-type.enum";
import { tableColumn } from "../../ui/table/table-column.const";
import { SortMeta } from "primeng/api";
import { ButtonDirective } from "primeng/button";
import { Company } from "app/company/company.model";
import { Group } from "../group/group.model";
import { Team } from "app/team/team.model";
import { PageTitleComponent } from "../../ui/page-title/page-title.component";
import { UserGroupSelectComponent } from "../../ui/dropdown/user-group-select/user-group-select.component";
import { CompanyMultiselectComponent } from "../../ui/dropdown/company-multiselect/company-multiselect.component";
import { TeamMultiselectComponent } from "../../ui/dropdown/team-multiselect/team-multiselect.component";
import { NgxPermissionsModule } from "ngx-permissions";
import { FormsModule } from "@angular/forms";
import { Checkbox } from "primeng/checkbox";
import { Bind } from "primeng/bind";
import { TableComponent } from "../../ui/table/table.component";
import { SearchBoxComponent } from "../../ui/search-box/search-box.component";
import { TableSelectionBarComponent } from "../../ui/table-selection-bar/table-selection-bar.component";
import { ITableColumn } from "../../ui/table/table-column.interface";

@Component({
  selector: "app-user-list",
  styleUrls: ["./user-list.component.scss"],
  templateUrl: "./user-list.component.html",
  providers: [
    GridParams,
    {
      provide: GridParams.DEFAULTS,
      useValue: {
        "sorting[username]": null,
      },
    },
    {
      provide: GridParams.STORAGE_KEY,
      useValue: "user-list",
    },
    GridSortingService,
    {
      provide: GridSortingService.SORTING_FIELDS,
      useValue: ["id", "username"],
    },
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ButtonDirective,
    PageTitleComponent,
    UserGroupSelectComponent,
    CompanyMultiselectComponent,
    TeamMultiselectComponent,
    NgxPermissionsModule,
    FormsModule,
    Bind,
    EmailEditorComponent,
    TableComponent,
    Checkbox,
    SearchBoxComponent,
    TableSelectionBarComponent,
  ],
})
export class UserListComponent
  extends CrudAbstractListComponent
  implements OnInit
{
  private notifyService = inject(NotifyService);
  private userService = inject(UserService);
  private emailRepository = inject(EmailEditorRepository);

  public readonly EmailEditorComponent = viewChild<EmailEditorComponent>(
    "EmailEditorComponent",
  );

  public role = role;
  public selectedUsers: User[] = [];
  public stats: UserStats = null;
  public showOnlyActiveUsers = false;
  public tableColumn = tableColumn;
  public emailLoading = false;
  public teamIdsFilter: number[] = null;
  public companyIdsFilter: number[] = null;
  public groupIdsFilter: number[] = null;
  public columns: ITableColumn[] = [];

  private userRepository: UserRepository;

  constructor() {
    const repository = inject(UserRepository);
    super();
    this.repository = repository;
    this.userRepository = repository;
  }

  ngOnInit() {
    super.ngOnInit();
    this.userService.getUser().then((user) => {
      this.user = user;
      this.updateColumns();
    });
    this.loadParam();
    this.loadStats();
    this.updateColumns();
  }

  private updateColumns(): void {
    let companyColumn = [];
    if (this.user?.hasCompanies()) {
      companyColumn = [
        { header: "Company", field: "companyName", sortable: true },
      ];
    }

    this.columns = [
      { header: "Name", field: "fullName", sortable: true },
      { header: "Team", field: "team", type: tableColumn.team, sortable: true },
      {
        header: "Organization",
        field: "organization",
        type: tableColumn.organization,
        sortable: true,
      },
      { header: "E-mail", field: "email", sortable: true },
      {
        header: "Role",
        field: "group",
        type: tableColumn.group,
        sortable: true,
      },
      { header: "Active", field: "enabled", type: tableColumn.boolean },
      {
        header: "Email integration",
        field: "emailIntegrationConnected",
        type: tableColumn.boolean,
      },
      ...companyColumn,
      { header: "Last Activity", field: "activeAt", sortable: true },
    ];
  }

  private loadStats() {
    this.subscription.add(
      this.userRepository.getStats().subscribe((stats) => {
        this.stats = stats;
      }),
    );
  }

  public openItem(model?: CrudAbstractModel): void {
    if (this.canEditUsers()) {
      super.openItem(model);
    }
  }

  public clearSelection() {
    this.selectedUsers = [];
  }

  public selectAll() {
    this.selectedUsers = TableSelectionBarComponent.selectPage(
      this.selectedUsers,
      this.items.items,
    );
  }

  public sendEmail() {
    this.emailLoading = true;
    this.subscription.add(
      this.emailRepository
        .send(
          "user",
          this.selectedUsers.map((user) => user.getId()),
          this.EmailEditorComponent().form.value,
        )
        .subscribe({
          next: (response) => {
            this.emailLoading = false;
            this.notifyService.toast(TOAST.INFO, "Successfully sent emails");
            this.EmailEditorComponent().close();
          },
          error: (error) => {
            this.emailLoading = false;
            new BackendErrorPipe().transform(error, "string");
          },
        }),
    );
  }

  public toggleActiveUsers() {
    this.showOnlyActiveUsers = !this.showOnlyActiveUsers;

    if (!this.showOnlyActiveUsers) {
      this.gridParams.setParam("criteria[enabled]", null);

      return;
    }
    this.gridParams.setParams({
      page: 1,
      "criteria[enabled]": this.showOnlyActiveUsers,
    });
  }

  public isClearFiltersVisible(): boolean {
    return !!(
      this.groupIdsFilter?.length ||
      this.companyIdsFilter?.length ||
      this.teamIdsFilter?.length ||
      this.showOnlyActiveUsers
    );
  }

  public clearFilters(): void {
    this.groupIdsFilter = [];
    this.companyIdsFilter = [];
    this.teamIdsFilter = [];
    this.showOnlyActiveUsers = false;
    this.gridParams.setParams({
      page: 1,
      "criteria[groups]": null,
      "criteria[companies]": null,
      "criteria[teams]": null,
      "criteria[enabled]": null,
    });
  }

  public sortGrid({ field, order }: SortMeta) {
    this.clearOtherSortingParams();
    super.sortGrid({ field, order });
  }

  public setUserListCompanies(companies: Company[]) {
    this.companyIdsFilter = companies.map((company) => company.getId());
    this.gridParams.setParams({
      page: 1,
      "criteria[companies]":
        this.companyIdsFilter && this.companyIdsFilter.length
          ? this.companyIdsFilter.join(",")
          : null,
    });
  }

  public setUserListGroups(groups: Group[]) {
    this.groupIdsFilter = groups.map((group) => group.getId());
    this.gridParams.setParams({
      page: 1,
      "criteria[groups]":
        this.groupIdsFilter && this.groupIdsFilter.length
          ? this.groupIdsFilter.join(",")
          : null,
    });
  }

  public setUserListTeams(teams: Team[]) {
    this.teamIdsFilter = teams.map((team) => team.getId());
    this.gridParams.setParams({
      page: 1,
      "criteria[teams]":
        this.teamIdsFilter && this.teamIdsFilter.length
          ? this.teamIdsFilter.join(",")
          : null,
    });
  }

  protected loadParam() {
    this.showOnlyActiveUsers = this.gridParams.getParam("criteria[enabled]");
    this.teamIdsFilter = this.gridParams.getParam("criteria[teams]")
      ? this.gridParams
          .getParam("criteria[teams]")
          .split(",")
          .map((id) => Number(id))
      : [];
    this.companyIdsFilter = this.gridParams.getParam("criteria[companies]")
      ? this.gridParams
          .getParam("criteria[companies]")
          .split(",")
          .map((id) => Number(id))
      : [];
    this.groupIdsFilter = this.gridParams.getParam("criteria[groups]")
      ? this.gridParams
          .getParam("criteria[groups]")
          .split(",")
          .map((id) => Number(id))
      : [];
  }

  private canEditUsers(): boolean {
    return this.user && this.user.hasRole(role.organizationAdmin);
  }

  private clearOtherSortingParams() {
    const paramKeys = Object.keys(this.gridParams.params);
    paramKeys.forEach((paramKey) => {
      if (/^sorting/.test(paramKey)) {
        this.gridParams.unsetParam(paramKey);
      }
    });
  }
}
